/**
 * 共享麦克风模块
 * 负责管理麦克风输入
 * 
 * 设计特点：
 * - 全局唯一，所有设备共享
 * - 支持设备间无缝切换
 * - 实时音频电平监测
 */


import { settingsService } from '../settingsService';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import { applyMediaDevicesPolyfill } from '../../utils/mediaDevicesPolyfill';

export interface MicrophoneConfig {
  sampleRate: number;
  channelCount: number;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

export interface MicrophoneStatus {
  hasPermission: boolean;
  isConnected: boolean;
  activeDeviceId: string | null;
  audioLevel: number;

}

export class MicrophoneModule {
  private audioContext: AudioContext;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private activeDevice: any = null;
  
  // AudioWorklet支持
  private audioWorkletNode: AudioWorkletNode | null = null;
  private isWorkletSupported = false;
  private workletInitialized = false;
  /**
   * iOS Safari / 全平台 WebKit：仅 source→Worklet 且 Worklet 输出悬空时，渲染引擎可能不拉取节点，process() 不跑则无上行 PCM。
   * Worklet → Gain(0) → destination 形成完整图，扬声器无泄漏。
   */
  private workletSilentGain: GainNode | null = null;
  
  // 音频分析
  private analyser: AnalyserNode;
  private audioLevelData!: Uint8Array;
  private audioLevel = 0;
  private animationFrame: number | null = null;
  
  // 事件系统
  private eventListeners: Map<string, Function[]> = new Map();
  
  // 配置
  private config: MicrophoneConfig = {
    sampleRate: 48000,
    channelCount: 2,
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false
  };
  
  /**
   * 从设置服务获取音频配置
   */
  private getAudioConfig(): MicrophoneConfig {
    const audioSettings = settingsService.getAudioSettings();
    return {
      sampleRate: audioSettings.sampleRate,
      channelCount: audioSettings.channels,
      echoCancellation: audioSettings.echoCancellation,
      noiseSuppression: audioSettings.noiseSuppression,
      autoGainControl: audioSettings.autoGainControl
    };
  }

  /**
   * 获取指定的输入设备ID
   */
  private getInputDeviceId(): string | null {
    const audioSettings = settingsService.getAudioSettings();
    return audioSettings.inputDeviceId && audioSettings.inputDeviceId !== 'default' 
      ? audioSettings.inputDeviceId 
      : null;
  }
  
  constructor(audioContext: AudioContext) {
    if (!audioContext) {
      throw new Error('AudioContext不能为null或undefined');
    }
    
    this.audioContext = audioContext;
    
    // 检查AudioWorklet支持
    try {
      this.isWorkletSupported = !!(this.audioContext && this.audioContext.audioWorklet);
    } catch (error) {
      console.warn('MicrophoneModule: AudioWorklet支持检查失败:', error);
      this.isWorkletSupported = false;
    }
    
    // 创建音频分析器
    if (!this.audioContext) {
      throw new Error('AudioContext在创建分析器时已变为null');
    }
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.8;
    const binCount = this.analyser.frequencyBinCount;
    this.audioLevelData = new Uint8Array(new ArrayBuffer(binCount));
    
    console.log('MicrophoneModule: 麦克风模块初始化完成', {
      audioWorkletSupported: this.isWorkletSupported
    });
  }
  
  /**
   * 初始化AudioWorklet
   */
  async initializeAudioWorklet(): Promise<void> {
    if (!this.isWorkletSupported || this.workletInitialized) {
      return;
    }
    
    try {
      console.log('MicrophoneModule: 开始初始化AudioWorklet...');
      
      const possiblePaths = [
        publicAssetUrl('/audio-processor.js'),
        publicAssetUrl('/audio-processor-simple.js')
      ];
      let moduleLoaded = false;
      let lastError: Error | null = null;
      for (const path of possiblePaths) {
        try {
          await this.audioContext.audioWorklet.addModule(path);
          moduleLoaded = true;
          console.log('MicrophoneModule: AudioWorklet 模块已加载:', path);
          break;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          console.warn('MicrophoneModule: 加载失败，尝试下一路径:', path, lastError);
        }
      }
      if (!moduleLoaded) {
        throw lastError || new Error('无法加载 AudioWorklet 模块');
      }
      
      // 创建AudioWorklet节点
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: this.config.channelCount,
        processorOptions: {
          bufferSize: 4096,
          channels: this.config.channelCount,
          sampleRate: this.config.sampleRate,
          // 电流声抑制配置
          noiseSuppression: true,
          noiseGate: 0.001,
          softClipping: true,
          dithering: true
        }
      });
      
      // 监听来自AudioWorklet的消息
      this.audioWorkletNode.port.onmessage = (event) => {
        this.handleWorkletMessage(event.data);
      };

      this.workletSilentGain = this.audioContext.createGain();
      this.workletSilentGain.gain.value = 0;
      this.audioWorkletNode.connect(this.workletSilentGain);
      this.workletSilentGain.connect(this.audioContext.destination);
      
      this.workletInitialized = true;
      console.log('MicrophoneModule: ✅ AudioWorklet 初始化成功 - 将使用高性能音频处理（已启用电流声抑制，已禁用音频回环）');
      
    } catch (error) {
      console.error('MicrophoneModule: ❌ AudioWorklet初始化失败:', error);
      console.log('MicrophoneModule: ⚠️ 将回退到 ScriptProcessor 进行音频处理');
      this.isWorkletSupported = false;
      if (this.workletSilentGain) {
        try {
          this.workletSilentGain.disconnect();
        } catch {
          /* ignore */
        }
        this.workletSilentGain = null;
      }
      this.audioWorkletNode = null;
    }
  }
  
  /**
   * 处理来自AudioWorklet的消息
   */
  private handleWorkletMessage(data: any): void {
    try {
      switch (data.type) {
        case 'audioData':
          // 处理录音数据
          this.handleRecordingData(data.audioData, data.timestamp, data.level);
          break;
          
        case 'audioLevel':
          // 处理音频电平
          this.handleAudioLevel(data.level, data.timestamp);
          break;

        case 'testResponse':
          // AudioManager 自检消息，无需处理
          break;
          
        default:
          console.warn('MicrophoneModule: 未知的Worklet消息类型:', data.type);
      }
    } catch (error) {
      console.error('MicrophoneModule: 处理Worklet消息失败:', error);
    }
  }
  
  /**
   * 处理录音数据
   */
  private handleRecordingData(audioData: ArrayBuffer, timestamp: number, level: number): void {
    try {
      // 触发音频数据事件
      this.emit('audioData', audioData);
      
    } catch (error) {
      console.error('MicrophoneModule: 处理录音数据失败:', error);
    }
  }
  
  /**
   * 处理音频电平
   */
  private handleAudioLevel(level: number, timestamp: number): void {
    try {
      this.audioLevel = level;
      
      // 触发音频电平事件
      this.emit('audioLevel', level);
      
    } catch (error) {
      console.error('MicrophoneModule: 处理音频电平失败:', error);
    }
  }
  
  /**
   * 事件监听
   */
  on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }
  
  /**
   * 移除事件监听
   */
  off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  /**
   * 触发事件
   */
  private emit(event: string, ...args: any[]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`MicrophoneModule: 事件处理错误 (${event}):`, error);
        }
      });
    }
  }
  
  /**
   * 请求麦克风权限
   */
  async requestPermission(): Promise<void> {
    try {
      // 更新配置
      this.config = this.getAudioConfig();
      console.log('MicrophoneModule: 请求麦克风权限...');
      console.log('MicrophoneModule: 配置:', this.config);
      
      // 检查浏览器兼容性
      this.checkBrowserCompatibility();
      
      // 先尝试获取物理麦克风设备
      await this.requestPhysicalMicrophoneFirst();
      
    } catch (error) {
      console.error('MicrophoneModule: 请求麦克风权限失败:', error);
      this.handleMicrophoneError(error as Error);
      throw error;
    }
  }
  
  /**
   * 优先请求物理麦克风设备
   */
  async requestPhysicalMicrophoneFirst(): Promise<void> {
    try {
      // 获取可用设备列表
      const devices = await this.getAvailableAudioInputDevices();
      
      if (devices.length === 0) {
        console.warn('MicrophoneModule: ⚠️ 未找到任何音频输入设备');
        throw new Error('未找到可用的音频输入设备');
      }
      
      console.log('MicrophoneModule: 找到的音频设备:', devices.map(d => ({
        deviceId: d.deviceId,
        label: d.label,
        isDefault: d.deviceId === 'default'
      })));
      
      // 检查是否有指定的输入设备
      const specifiedDeviceId = this.getInputDeviceId();
      if (specifiedDeviceId) {
        const specifiedDevice = devices.find(device => device.deviceId === specifiedDeviceId);
        if (specifiedDevice) {
          console.log('MicrophoneModule: 🎤 使用指定的输入设备:', specifiedDevice.label);
          await this.createAudioStreamWithDevice(specifiedDeviceId);
          return;
        } else {
          console.warn('MicrophoneModule: ⚠️ 指定的输入设备不存在，回退到默认选择');
        }
      }
      
      // 查找物理麦克风设备
      const physicalMic = devices.find(device => 
        !device.label.includes('DAX') && 
        !device.label.includes('Virtual') &&
        !device.label.includes('CABLE') &&
        !device.label.includes('VoiceMeeter') &&
        (device.label.toLowerCase().includes('microphone') ||
         device.label.toLowerCase().includes('mic') ||
         device.label.toLowerCase().includes('headset') ||
         device.label.toLowerCase().includes('webcam') ||
         device.deviceId === 'default')
      );
      
      if (physicalMic) {
        console.log('MicrophoneModule: 🎤 找到物理麦克风设备:', physicalMic.label);
        console.log('MicrophoneModule: 使用物理麦克风进行PTT');
        
        // 使用物理麦克风创建音频流
        await this.createAudioStreamWithDevice(physicalMic.deviceId);
      } else {
        console.warn('MicrophoneModule: ⚠️ 未找到物理麦克风，尝试使用第一个可用设备');
        
        // 使用第一个可用设备
        const firstDevice = devices[0];
        console.log('MicrophoneModule: 使用第一个可用设备:', firstDevice.label);
        await this.createAudioStreamWithDevice(firstDevice.deviceId);
      }
      
    } catch (error) {
      console.warn('MicrophoneModule: 物理麦克风请求失败，回退到默认设备:', error);
      await this.createAudioStreamWithDefaultDevice();
    }
  }
  
  /**
   * 使用默认设备创建音频流
   */
  async createAudioStreamWithDefaultDevice(): Promise<void> {
    try {
      // 首先尝试使用完整配置
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: { ideal: this.config.sampleRate },
          channelCount: { ideal: this.config.channelCount },
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl
        }
      });
      
      await this.setupAudioStream();
    } catch (error) {
      console.warn('MicrophoneModule: 使用完整配置失败，尝试使用最小配置:', error);
      
      // 如果失败，尝试使用最小配置
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: true // 使用最简单的音频配置
        });
        
        await this.setupAudioStream();
      } catch (fallbackError) {
        console.error('MicrophoneModule: 最小配置也失败:', fallbackError);
        throw fallbackError;
      }
    }
  }
  
  /**
   * 设置音频流和相关节点
   */
  async setupAudioStream(): Promise<void> {
    console.log('MicrophoneModule: 麦克风权限获取成功');
    console.log('MicrophoneModule: 音频流信息:', {
      id: this.stream!.id,
      active: this.stream!.active,
      tracks: this.stream!.getTracks().map(track => ({
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        label: track.label,
        settings: track.getSettings()
      }))
    });
    
    // 创建音频源节点
    this.source = this.audioContext.createMediaStreamSource(this.stream!);
    
    // 连接到分析器
    this.source.connect(this.analyser);
    
    // 初始化AudioWorklet
    if (this.isWorkletSupported) {
      await this.initializeAudioWorklet();
    } else {
      console.log('MicrophoneModule: ⚠️ AudioWorklet 不支持，将使用 ScriptProcessor');
    }
    
    // 开始音频电平监测
    this.startAudioLevelMonitoring();
    
    console.log('MicrophoneModule: 麦克风模块准备就绪');
  }
  
  /**
   * 检查浏览器兼容性
   */
  private checkBrowserCompatibility(): void {
    applyMediaDevicesPolyfill();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isEdgeOnIOS = /Edg/.test(navigator.userAgent) && /iPhone|iPad|iPod/.test(navigator.userAgent);
    
    console.log('MicrophoneModule: 设备检测:', { isIOS, isSafari, isEdgeOnIOS });
    console.log('MicrophoneModule: 浏览器信息:', navigator.userAgent);
    console.log('MicrophoneModule: 当前协议:', window.location.protocol);
    console.log('MicrophoneModule: 当前主机:', window.location.hostname);
    
    // 检查MediaDevices支持
    if (!navigator.mediaDevices) {
      if (isIOS && (isSafari || isEdgeOnIOS)) {
        throw new Error('iOS设备上的Safari/Edge浏览器对音频录制有严格限制，建议使用HTTPS环境或桌面浏览器');
      }
      throw new Error('浏览器不支持MediaDevices API');
    }
    
    if (!navigator.mediaDevices.getUserMedia) {
      throw new Error('浏览器不支持getUserMedia API');
    }
    
    // 检查HTTPS环境
    if (window.location.protocol !== 'https:' && 
        window.location.hostname !== 'localhost' && 
        window.location.hostname !== '127.0.0.1') {
      console.warn('MicrophoneModule: 警告 - 在非HTTPS环境下可能无法访问麦克风');
      
      if (isIOS) {
        console.warn('MicrophoneModule: iOS设备在非HTTPS环境下通常无法访问麦克风');
      }
    }
  }
  
  /**
   * 处理麦克风错误
   */
  private handleMicrophoneError(error: Error): void {
    console.error('MicrophoneModule: 麦克风错误详情:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    // 根据错误类型提供具体的错误信息和解决建议
    switch (error.name) {
      case 'NotAllowedError':
        throw new Error('麦克风权限被拒绝，请允许浏览器访问麦克风。请检查浏览器地址栏的麦克风权限设置。');
      case 'NotFoundError':
        throw new Error('未找到麦克风设备。请检查设备连接或尝试刷新页面。');
      case 'NotSupportedError':
        throw new Error('浏览器不支持音频录制功能。请使用Chrome、Firefox或Edge浏览器。');
      case 'SecurityError':
        throw new Error('安全错误：可能需要在HTTPS环境下运行，或者麦克风被其他应用占用。');
      case 'AbortError':
        throw new Error('用户取消了麦克风权限请求。请重新点击PTT按钮。');
      case 'NotReadableError':
        throw new Error('麦克风设备无法启动，可能被其他应用程序占用。请关闭其他使用麦克风的应用（如Zoom、Teams等）后重试。');
      case 'OverconstrainedError':
        throw new Error('麦克风设备不支持请求的配置。系统将尝试使用兼容的配置。');
      default:
        throw new Error(`无法访问麦克风: ${error.message}。请检查设备连接和浏览器权限设置。`);
    }
  }
  
  /**
   * 连接到设备
   */
  connectToDevice(deviceManager: any): void {
    if (!this.source) {
      throw new Error('麦克风未初始化');
    }
    
    console.log(`MicrophoneModule: 连接到设备 - ${deviceManager.getDeviceId()}`);
    
    // 断开之前的设备
    if (this.activeDevice) {
      this.disconnectFromDevice(this.activeDevice);
    }
    
    // 连接到新设备
    this.activeDevice = deviceManager;
    const recordingInput = deviceManager.getRecordingInput();
    this.source.connect(recordingInput);
    
    console.log(`MicrophoneModule: 麦克风已连接到设备 - ${deviceManager.getDeviceId()}`);
  }
  
  /**
   * 从设备断开
   */
  disconnectFromDevice(deviceManager: any): void {
    if (!this.source) {
      return;
    }
    
    console.log(`MicrophoneModule: 从设备断开 - ${deviceManager.getDeviceId()}`);
    
    try {
      const recordingInput = deviceManager.getRecordingInput();
      this.source.disconnect(recordingInput);
    } catch (error) {
      console.warn('MicrophoneModule: 断开设备连接时发生警告:', error);
    }
    
    if (this.activeDevice === deviceManager) {
      this.activeDevice = null;
    }
    
    console.log(`MicrophoneModule: 已从设备断开 - ${deviceManager.getDeviceId()}`);
  }
  

  

  

  

  

  

  

  
  /**
   * 获取可用的音频输入设备
   */
  async getAvailableAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      console.log('🎤 可用的音频输入设备:');
      audioInputs.forEach((device, index) => {
        console.log(`${index + 1}. ${device.label || 'Unknown Device'} (${device.deviceId})`);
      });
      
      return audioInputs;
    } catch (error) {
      console.error('获取音频输入设备失败:', error);
      return [];
    }
  }
  
  /**
   * 诊断音频设备状态
   */
  async diagnoseAudioDevices(): Promise<{
    devices: MediaDeviceInfo[];
    permissions: PermissionState | null;
    browserSupport: boolean;
    recommendations: string[];
  }> {
    const result = {
      devices: [] as MediaDeviceInfo[],
      permissions: null as PermissionState | null,
      browserSupport: false,
      recommendations: [] as string[]
    };
    
    try {
      // 检查浏览器支持
      result.browserSupport = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      
      if (!result.browserSupport) {
        result.recommendations.push('浏览器不支持getUserMedia API，请使用现代浏览器');
        return result;
      }
      
      // 检查权限状态
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          result.permissions = permission.state;
          
          if (permission.state === 'denied') {
            result.recommendations.push('麦克风权限被拒绝，请在浏览器设置中允许麦克风访问');
          }
        } catch (error) {
          console.warn('无法查询麦克风权限状态:', error);
        }
      }
      
      // 获取设备列表
      result.devices = await this.getAvailableAudioInputDevices();
      
      if (result.devices.length === 0) {
        result.recommendations.push('未检测到音频输入设备，请检查设备连接');
      } else if (result.devices.length === 1 && result.devices[0].label === '') {
        result.recommendations.push('检测到音频设备但未获得权限，请允许浏览器访问麦克风');
      }
      
      // 检查是否有物理设备
      const hasPhysicalDevice = result.devices.some(device => 
        !device.label.includes('DAX') && 
        !device.label.includes('Virtual') &&
        !device.label.includes('CABLE') &&
        !device.label.includes('VoiceMeeter')
      );
      
      if (!hasPhysicalDevice) {
        result.recommendations.push('只检测到虚拟音频设备，可能影响录音质量');
      }
      
    } catch (error) {
      console.error('音频设备诊断失败:', error);
      result.recommendations.push('设备诊断失败，请刷新页面重试');
    }
    
    return result;
  }
  
  /**
   * 使用指定设备创建音频流
   */
  async createAudioStreamWithDevice(deviceId?: string): Promise<void> {
    try {
      console.log('🔄 使用指定设备创建音频流...', deviceId ? `设备ID: ${deviceId}` : '默认设备');
      
      // 停止当前音频流
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      
      // 构建音频约束 - 使用更宽松的约束
      const constraints: MediaStreamConstraints = {
        audio: deviceId ? {
          deviceId: { exact: deviceId },
          // 使用更宽松的音频配置，避免设备兼容性问题
          sampleRate: { ideal: this.config.sampleRate },
          channelCount: { ideal: this.config.channelCount },
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl
        } : {
          // 默认设备使用更宽松的约束
          sampleRate: { ideal: this.config.sampleRate },
          channelCount: { ideal: this.config.channelCount },
          echoCancellation: this.config.echoCancellation,
          noiseSuppression: this.config.noiseSuppression,
          autoGainControl: this.config.autoGainControl
        }
      };
      
      console.log('MicrophoneModule: 音频约束:', constraints);
      
      // 创建新的音频流
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ 新音频流创建成功:', {
        id: this.stream.id,
        active: this.stream.active,
        tracks: this.stream.getTracks().map(track => ({
          kind: track.kind,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          label: track.label,
          settings: track.getSettings()
        }))
      });
      
      // 重新创建音频源节点
      if (this.source) {
        this.source.disconnect();
      }
      
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.source.connect(this.analyser);
      
      // 重新启动音频电平监测
      this.stopAudioLevelMonitoring();
      this.startAudioLevelMonitoring();
      
      // 重新初始化AudioWorklet（如果支持）
      if (this.isWorkletSupported) {
        await this.initializeAudioWorklet();
      }
      
      console.log('✅ 音频源节点重新创建完成');
      
    } catch (error) {
      console.error('使用指定设备创建音频流失败:', error);
      throw error;
    }
  }
  
  /**
   * 测试重新创建音频流
   */
  async testRecreateAudioStream(): Promise<void> {
    try {
      console.log('🔄 开始测试重新创建音频流...');
      
      // 停止当前音频流
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }
      
      // 重新请求音频权限并创建流
      await this.requestPermission();
      
      console.log('✅ 音频流重新创建完成');
      
    } catch (error) {
      console.error('重新创建音频流失败:', error);
      throw error;
    }
  }
  
  /**
   * 测试不同的音频输入设备
   */
  async testDifferentAudioDevice(): Promise<void> {
    try {
      console.log('🔄 开始测试不同的音频输入设备...');
      
      const devices = await this.getAvailableAudioInputDevices();
      
      if (devices.length === 0) {
        console.warn('未找到可用的音频输入设备');
        return;
      }
      
      // 找到第一个不是DAX设备的设备
      const nonDaxDevice = devices.find(device => 
        !device.label.includes('DAX') && 
        !device.label.includes('Virtual') &&
        (device.label.toLowerCase().includes('microphone') ||
         device.label.toLowerCase().includes('mic') ||
         device.label.toLowerCase().includes('headset') ||
         device.deviceId === 'default')
      );
      
      if (nonDaxDevice) {
        console.log('🎤 尝试使用非虚拟音频设备:', nonDaxDevice.label);
        
        // 使用新设备创建音频流
        await this.createAudioStreamWithDevice(nonDaxDevice.deviceId);
        
        console.log('✅ 音频设备切换完成');
      } else {
        console.warn('未找到合适的替代音频设备，所有设备都是虚拟设备');
        
        // 尝试使用默认设备
        if (devices.length > 1) {
          const alternativeDevice = devices[1]; // 使用第二个设备
          console.log('🎤 尝试使用替代设备:', alternativeDevice.label);
          
          await this.createAudioStreamWithDevice(alternativeDevice.deviceId);
        }
      }
      
    } catch (error) {
      console.error('测试不同音频设备失败:', error);
      throw error;
    }
  }
  
  /**
   * 调试音频路径
   */
  private debugAudioPath(): void {
    console.log('=== 🔍 音频路径调试信息 ===');
    
    // 麦克风流状态
    console.log('📹 麦克风流状态:', {
      存在: !!this.stream,
      id: this.stream?.id,
      激活: this.stream?.active,
      轨道数: this.stream?.getTracks().length,
      轨道详情: this.stream?.getTracks().map(track => ({
        类型: track.kind,
        已启用: track.enabled,
        静音: track.muted,
        状态: track.readyState,
        标签: track.label,
        设置: track.getSettings()
      }))
    });
    
    // 音频上下文状态
    console.log('🎵 音频上下文:', {
      状态: this.audioContext.state,
      采样率: this.audioContext.sampleRate,
      当前时间: this.audioContext.currentTime.toFixed(2),
      输出设备: this.audioContext.destination,
      最大通道数: this.audioContext.destination.maxChannelCount
    });
    
    // 音频源节点
    console.log('🎤 音频源节点:', {
      存在: !!this.source,
      输入数: this.source?.numberOfInputs,
      输出数: this.source?.numberOfOutputs,
      上下文: this.source?.context === this.audioContext
    });
    

    
    // 音频数据检测
    if (this.analyser) {
      const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(dataArray);
      const hasAudioData = dataArray.some(value => value > 0);
      const maxValue = Math.max.apply(null, Array.from(dataArray));
      const avgValue = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      
      console.log('📊 音频数据分析:', {
        检测到音频: hasAudioData,
        最大值: maxValue,
        平均值: avgValue.toFixed(2),
        音频电平: this.audioLevel.toFixed(3),
        分析器连接状态: '已连接到麦克风源'
      });
    }
    
    // 音频路径
    console.log('🎯 音频路径:');
    console.log('   麦克风流 -> MediaStreamAudioSourceNode -> 录音模块');
    
    // 故障排除建议
    console.log('🔧 故障排除建议:');
    if (!this.stream?.active) {
      console.log('   ❌ 麦克风流未激活 - 检查麦克风权限');
    }
    if (this.audioContext.state !== 'running') {
      console.log('   ❌ 音频上下文未运行 - 点击"恢复音频上下文"按钮');
    }

    if (this.audioLevel === 0) {
      console.log('   ⚠️ 未检测到音频输入 - 检查麦克风是否工作，尝试说话');
    }
    
    console.log('================================');
  }
  
  /**
   * 开始音频电平监测
   */
  private startAudioLevelMonitoring(): void {
    if (!this.analyser) {
      return;
    }
    
    const updateAudioLevel = () => {
      // 新版 lib.dom 中 getByteFrequencyData 要求 Uint8Array<ArrayBuffer>，而 TS 对 new Uint8Array(...) 常推断为 ArrayBufferLike；缓冲区实际为 ArrayBuffer
      (this.analyser as AnalyserNode & { getByteFrequencyData(array: Uint8Array): void }).getByteFrequencyData(
        this.audioLevelData
      );
      
      // 计算平均音频电平
      let sum = 0;
      for (let i = 0; i < this.audioLevelData.length; i++) {
        sum += this.audioLevelData[i];
      }
      this.audioLevel = sum / this.audioLevelData.length / 255; // 归一化到0-1
      
      // 继续监测
      this.animationFrame = requestAnimationFrame(updateAudioLevel);
    };
    
    updateAudioLevel();
    console.log('MicrophoneModule: 音频电平监测已开始');
  }
  
  /**
   * 停止音频电平监测
   */
  private stopAudioLevelMonitoring(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.audioLevel = 0;
    console.log('MicrophoneModule: 音频电平监测已停止');
  }
  
  /**
   * 获取音频电平
   */
  getAudioLevel(): number {
    return this.audioLevel;
  }
  
  /**
   * 获取状态
   */
  getStatus(): MicrophoneStatus {
    return {
      hasPermission: !!this.stream,
      isConnected: !!this.source,
      activeDeviceId: this.activeDevice?.getDeviceId() || null,
      audioLevel: this.audioLevel,

    };
  }
  
  /**
   * 设置配置
   */
  setConfig(config: Partial<MicrophoneConfig>): void {
    this.config = { ...this.config, ...config };
    console.log('MicrophoneModule: 配置已更新:', this.config);
  }
  
  /**
   * 获取配置
   */
  getConfig(): MicrophoneConfig {
    return { ...this.config };
  }
  
  /**
   * 检查是否有权限
   */
  hasPermission(): boolean {
    return !!this.stream && this.stream.active;
  }
  
  /**
   * 获取媒体流
   */
  getMediaStream(): MediaStream | null {
    return this.stream;
  }
  
  /**
   * 获取当前音频设备信息
   */
  getCurrentAudioDevice(): { label: string; deviceId: string; isPhysical: boolean } | null {
    if (!this.stream) {
      return null;
    }
    
    const audioTrack = this.stream.getAudioTracks()[0];
    if (!audioTrack) {
      return null;
    }
    
    const isPhysical = !audioTrack.label.includes('DAX') && 
                      !audioTrack.label.includes('Virtual') &&
                      !audioTrack.label.includes('CABLE') &&
                      !audioTrack.label.includes('VoiceMeeter');
    
    return {
      label: audioTrack.label || 'Unknown Device',
      deviceId: audioTrack.getSettings().deviceId || 'unknown',
      isPhysical: isPhysical
    };
  }
  
  /**
   * 获取音频源节点
   */
  getAudioSource(): MediaStreamAudioSourceNode | null {
    return this.source;
  }
  
  /**
   * 销毁资源
   */
  dispose(): void {
    console.log('MicrophoneModule: 开始销毁资源...');
    
    // 停止音频电平监测
    this.stopAudioLevelMonitoring();
    
    // 断开AudioWorklet节点
    if (this.audioWorkletNode) {
      try {
        this.audioWorkletNode.disconnect();
        this.audioWorkletNode = null;
      } catch (error) {
        console.warn('MicrophoneModule: 断开AudioWorklet节点时发生警告:', error);
      }
    }
    
    // 断开所有连接
    if (this.source) {
      try {
        this.source.disconnect();
      } catch (error) {
        console.warn('MicrophoneModule: 断开音频源时发生警告:', error);
      }
      this.source = null;
    }
    
    // 重置AudioWorklet状态
    this.workletInitialized = false;
    
    // 停止媒体流
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
        console.log('MicrophoneModule: 音频轨道已停止:', track.label);
      });
      this.stream = null;
    }
    

    
    // 清理分析器
    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch (error) {
        console.warn('MicrophoneModule: 断开分析器时发生警告:', error);
      }
    }
    
    this.activeDevice = null;
    this.audioLevel = 0;
    
    console.log('MicrophoneModule: 资源销毁完成');
  }
}
